import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Activity,
  BarChart3,
  ChevronRight,
  Database,
  ExternalLink,
  FileText,
  Layers,
  Play,
  RefreshCw,
  Search,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'
import InlineError from '../ui/InlineError'

const TERMS_LIMIT_OPTIONS = [20, 50, 100, 200]

const INSURER_OPTIONS = [
  ['SAMSUNG_FIRE', '삼성화재'],
  ['KB_FIRE', 'KB손해보험'],
  ['HANWHA_FIRE', '한화손해보험'],
  ['LINA_LIFE', '라이나생명'],
  ['HYUNDAI_FIRE', '현대해상'],
  ['DB_FIRE', 'DB손해보험'],
  ['MERITZ_FIRE', '메리츠화재'],
  ['AXA_FIRE', 'AXA손해보험'],
  ['LOTTE_FIRE', '롯데손해보험'],
  ['HANA_FIRE', '하나손해보험'],
  ['HK_FIRE', '흥국화재'],
  ['AIG_FIRE', 'AIG손해보험'],
  ['SHINHAN_EZ_FIRE', '신한EZ손해보험'],
  ['CHUBB_FIRE', '처브손해보험'],
  ['MG_FIRE', 'MG손해보험'],
  ['CARROT_FIRE', '캐롯손해보험'],
]

const INSURER_LABELS = Object.fromEntries([
  ...INSURER_OPTIONS,
  ['DB', 'DB손해보험'],
  ['MERITZ', '메리츠화재'],
  ['KB_NONLIFE', 'KB손해보험'],
  ['HANWHA', '한화손해보험'],
  ['AXA', 'AXA손해보험'],
  ['AXA_NONLIFE', 'AXA손해보험'],
  ['LOTTE', '롯데손해보험'],
  ['LOTTE_NONLIFE', '롯데손해보험'],
  ['HANA', '하나생명'],
  ['HANA_NONLIFE', '하나손해보험'],
  ['HEUNGKUK_FIRE', '흥국화재'],
  ['AIG_KOREA', 'AIG손해보험'],
  ['SHINHAN_EZ', '신한EZ손해보험'],
  ['CHUBB_KOREA', '처브손해보험'],
  ['MG_NONLIFE', 'MG손해보험'],
  ['CARROT_NONLIFE', '캐롯손해보험'],
])

const ITEM_STATUS_LABELS = {
  PENDING: '대기',
  DISCOVERED: '발견',
  DOWNLOADING: '다운로드 중',
  DOWNLOADED: '다운로드 완료',
  COMPLETED: '완료',
  UNCHANGED: '기존 동일',
  SKIPPED: '제외',
  FAILED: '실패',
  DOWNLOAD_FAILED: '다운로드 실패',
  PARSE_FAILED: '파싱 실패',
  VALIDATION_FAILED: '검증 실패',
  EXCEPTION: '예외',
  NEEDS_ADAPTER: '수집기 필요',
}

const PARSE_STATUS_LABELS = {
  PENDING: '대기',
  PARSING: '파싱 중',
  PARSED: '파싱 완료',
  OCR_REQUIRED: 'OCR 필요',
  OCR_RUNNING: 'OCR 중',
  FAILED: '파싱 실패',
}

const RUN_STATUS_LABELS = {
  PENDING: '대기',
  RUNNING: '수집 중',
  COMPLETED: '완료',
  NEEDS_ADAPTER: '수집기 필요',
  FAILED: '실패',
  PARTIAL: '일부 실패',
}

const OPERATIONAL_STATUS_LABELS = {
  READY: '정상',
  RUNNING: '처리 중',
  PENDING: '대기 중',
  NEEDS_ADAPTER: '수집기 필요',
  FAILED: '오류',
  REVIEW_REQUIRED: '검토 필요',
  DISABLED: '비활성',
  EMPTY: '데이터 없음',
}

const EMBEDDING_STATUS_LABELS = {
  PENDING: '대기',
  READY: '완료',
  DONE: '완료',
  FAILED: '실패',
}

const CANDIDATE_STATUS_LABELS = {
  PENDING: '검토 대기',
  READY: '준비됨',
  GENERATED: '생성됨',
  APPROVED: '확정',
  REJECTED: '제외',
  FAILED: '실패',
}

function unwrapApiData(res) {
  const body = res?.data
  return body?.data !== undefined ? body.data : body
}

function toItems(value) {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.items)) return value.items
  if (Array.isArray(value?.candidates)) return value.candidates
  return []
}

function formatDateTime(value) {
  return value ? formatApiDateTimeDisplay(undefined, value) : '-'
}

function formatNumber(value) {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '-'
}

function statusLabel(value, labels) {
  if (!value) return '-'
  return labels[value] || '확인 필요'
}

function insurerLabel(code) {
  if (!code) return '-'
  return INSURER_LABELS[code] || '기타 보험사'
}

function collectionScheduleLabel(value) {
  const normalized = String(value || '').toLowerCase()
  if (normalized === 'daily') return '매일'
  if (normalized === 'weekly') return '매주'
  if (normalized === 'monthly') return '매월'
  if (normalized === 'hourly') return '매시간'
  if (normalized.includes('adapter')) return '수집기 준비 필요'
  if (normalized === 'manual') return '수동'
  return value ? '예약됨' : '수동'
}

function saleStartDateFromText(value) {
  const match = String(value || '').match(/(\d{4})[.-](\d{2})[.-](\d{2})/)
  return match ? `${match[1]}-${match[2]}-${match[3]}` : ''
}

function documentStatusLabel(item) {
  if (item?.parseStatus === 'PARSED') return '파싱 완료'
  if (item?.parseStatus === 'PARSING') return '파싱 중'
  if (item?.itemStatus === 'COMPLETED') return '수집 완료'
  if (item?.itemStatus === 'UNCHANGED') return '기존 문서'
  return statusLabel(item?.itemStatus, ITEM_STATUS_LABELS)
}

function chunkLabel(index) {
  const n = Number(index)
  return Number.isFinite(n) ? `본문 ${n + 1}` : '본문'
}

function matchReasonLabel(reason) {
  const value = String(reason || '').toLowerCase()
  if (value.includes('insurer')) return '보험사 일치'
  if (value.includes('product')) return '상품명 유사'
  if (value.includes('sale') || value.includes('date')) return '가입일 기준'
  if (value.includes('query') || value.includes('keyword')) return '검색어 일치'
  if (value.includes('status') || value.includes('parsed')) return '사용 가능'
  return '매칭 근거'
}

function Badge({ children, tone = 'slate' }) {
  const toneClass = tone === 'green'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : tone === 'red'
      ? 'border-red-200 bg-red-50 text-red-700'
      : tone === 'blue'
        ? 'border-sky-200 bg-sky-50 text-sky-700'
        : tone === 'amber'
          ? 'border-amber-200 bg-amber-50 text-amber-800'
          : 'border-slate-200 bg-slate-50 text-slate-700'
  return (
    <span className={`inline-flex min-h-[1.5rem] items-center rounded-full border px-2 text-xs font-semibold ${toneClass}`}>
      {children}
    </span>
  )
}

function statusTone(value) {
  if (['READY', 'COMPLETED', 'PARSED', 'DONE'].includes(value)) return 'green'
  if (String(value || '').includes('FAILED') || ['FAILED', 'EXCEPTION', 'VALIDATION_FAILED'].includes(value)) return 'red'
  if (['PENDING', 'PARSING', 'RUNNING', 'DOWNLOADING'].includes(value)) return 'blue'
  if (['UNCHANGED', 'OCR_REQUIRED', 'NEEDS_ADAPTER'].includes(value)) return 'amber'
  return 'slate'
}

function StatTile({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="mt-1 truncate text-2xl font-bold text-slate-950">{value}</div>
      {sub ? <div className="mt-1 truncate text-xs text-slate-500">{sub}</div> : null}
    </div>
  )
}

function EmptyState({ children }) {
  return (
    <div className="flex min-h-[10rem] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-sm font-medium text-slate-500">
      {children}
    </div>
  )
}

function DetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 text-sm">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="min-w-0 text-slate-900">{children || '-'}</div>
    </div>
  )
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      className={`inline-flex h-10 items-center rounded-lg px-4 text-sm font-semibold transition ${
        active ? 'bg-brand-navy text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export default function OpsTermsManagementPage() {
  const [activeTab, setActiveTab] = useState('status')
  const [ready, setReady] = useState(null)
  const [readyError, setReadyError] = useState('')
  const [collectionStatus, setCollectionStatus] = useState(null)
  const [statusLoading, setStatusLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [refreshingSourceId, setRefreshingSourceId] = useState(null)
  const [documents, setDocuments] = useState([])
  const [documentMeta, setDocumentMeta] = useState(null)
  const [documentsLoading, setDocumentsLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState('')

  const [filters, setFilters] = useState({
    insurerCode: '',
    q: '',
    status: 'COMPLETED',
    limit: 50,
  })
  const [selectedId, setSelectedId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [chunks, setChunks] = useState([])
  const [ruleCandidates, setRuleCandidates] = useState([])

  const [chunkQuery, setChunkQuery] = useState('보험금')
  const [chunkResults, setChunkResults] = useState([])
  const [chunkSearchLoading, setChunkSearchLoading] = useState(false)

  const [matchForm, setMatchForm] = useState({
    insurerCode: '',
    productName: '',
    saleDate: '',
    coverageName: '',
    query: '',
    limit: 5,
  })
  const [matchResult, setMatchResult] = useState(null)
  const [matchLoading, setMatchLoading] = useState(false)

  const selectedDocument = useMemo(
    () => documents.find((item) => String(item?.id) === String(selectedId)) || null,
    [documents, selectedId],
  )

  const statusItems = useMemo(() => toItems(collectionStatus), [collectionStatus])

  const loadReady = useCallback(async () => {
    setReadyError('')
    try {
      const res = await api.get('/terms/integrated/ready', { __silent: true })
      setReady(unwrapApiData(res) || null)
    } catch (e) {
      setReady(null)
      setReadyError(getApiErrorMessage(e, '통합약관 연결 상태를 확인하지 못했습니다.'))
    }
  }, [])

  const loadCollectionStatus = useCallback(async () => {
    setStatusLoading(true)
    setError('')
    try {
      const res = await api.get('/terms/integrated/collection-status', { __silent: true })
      setCollectionStatus(unwrapApiData(res) || null)
    } catch (e) {
      setCollectionStatus(null)
      setError(getApiErrorMessage(e, '약관 현황을 불러오지 못했습니다.'))
    } finally {
      setStatusLoading(false)
    }
  }, [])

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true)
    setError('')
    try {
      const params = { limit: filters.limit }
      if (filters.insurerCode) params.insurerCode = filters.insurerCode
      if (filters.q.trim()) params.q = filters.q.trim()
      if (filters.status) params.status = filters.status

      const res = await api.get('/terms/integrated/documents', { params, __silent: true })
      const payload = unwrapApiData(res) || {}
      const items = toItems(payload)
      setDocuments(items)
      setDocumentMeta(payload)
      setSelectedId((prev) => {
        if (prev && items.some((item) => String(item?.id) === String(prev))) return prev
        return items[0]?.id ?? null
      })
    } catch (e) {
      setDocuments([])
      setDocumentMeta(null)
      setError(getApiErrorMessage(e, '약관 문서를 불러오지 못했습니다.'))
    } finally {
      setDocumentsLoading(false)
    }
  }, [filters])

  const loadDocumentDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null)
      setChunks([])
      setRuleCandidates([])
      return
    }
    setDetailLoading(true)
    try {
      const [detailRes, chunksRes] = await Promise.all([
        api.get(`/terms/integrated/documents/${id}`, { __silent: true }),
        api.get(`/terms/integrated/documents/${id}/chunks`, { __silent: true }),
      ])
      setDetail(unwrapApiData(detailRes) || null)
      setChunks(toItems(unwrapApiData(chunksRes)))
      try {
        const candidatesRes = await api.get(`/terms/integrated/documents/${id}/rule-candidates`, { __silent: true })
        setRuleCandidates(toItems(unwrapApiData(candidatesRes)))
      } catch {
        setRuleCandidates([])
      }
    } catch (e) {
      setDetail(null)
      setChunks([])
      setRuleCandidates([])
      setError(getApiErrorMessage(e, '약관 상세를 불러오지 못했습니다.'))
    } finally {
      setDetailLoading(false)
    }
  }, [])

  const triggerCollectionRun = async (source) => {
    if (!source?.sourceId) return
    setRefreshingSourceId(source.sourceId)
    setStatusMessage('')
    setError('')
    try {
      await api.post(`/terms/integrated/collection-sources/${source.sourceId}/runs`, null, { __silent: true })
      setStatusMessage(`${insurerLabel(source.insurerCode)} 약관 갱신을 요청했습니다.`)
      await loadCollectionStatus()
    } catch (e) {
      setError(getApiErrorMessage(e, '약관 갱신을 요청하지 못했습니다.'))
    } finally {
      setRefreshingSourceId(null)
    }
  }

  const runChunkSearch = async () => {
    const q = chunkQuery.trim()
    if (!q) return
    setChunkSearchLoading(true)
    setError('')
    try {
      const params = { q, limit: 20 }
      if (filters.insurerCode) params.insurerCode = filters.insurerCode
      const res = await api.get('/terms/integrated/chunks/search', { params, __silent: true })
      setChunkResults(toItems(unwrapApiData(res)))
    } catch (e) {
      setChunkResults([])
      setError(getApiErrorMessage(e, '본문 검색에 실패했습니다.'))
    } finally {
      setChunkSearchLoading(false)
    }
  }

  const runMatch = async () => {
    setMatchLoading(true)
    setError('')
    try {
      const body = {
        insurerCode: matchForm.insurerCode || null,
        productName: matchForm.productName || null,
        saleDate: matchForm.saleDate || null,
        coverageName: matchForm.coverageName || null,
        query: matchForm.query || null,
        limit: Number(matchForm.limit || 5),
      }
      const res = await api.post('/terms/integrated/document-matches', body, { __silent: true })
      setMatchResult(unwrapApiData(res) || null)
    } catch (e) {
      setMatchResult(null)
      setError(getApiErrorMessage(e, '약관 매칭에 실패했습니다.'))
    } finally {
      setMatchLoading(false)
    }
  }

  const applySelectedDocumentToMatch = () => {
    const source = detail || selectedDocument
    if (!source) return
    setMatchForm((prev) => ({
      ...prev,
      insurerCode: source.insurerCode || '',
      productName: source.productName || '',
      saleDate: saleStartDateFromText(source.salePeriodText),
      coverageName: '',
      query: '',
    }))
    setMatchResult(null)
  }

  useEffect(() => {
    loadReady()
    loadCollectionStatus()
  }, [loadReady, loadCollectionStatus])

  useEffect(() => {
    loadDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadDocumentDetail(selectedId)
  }, [loadDocumentDetail, selectedId])

  const usableCount = documents.filter((item) => item?.parseStatus === 'PARSED' && Number(item?.chunkCount || 0) > 0).length
  const collectedCount = documents.filter((item) => ['COMPLETED', 'DOWNLOADED', 'UNCHANGED'].includes(item?.itemStatus)).length
  const busy = documentsLoading || detailLoading || statusLoading

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-slate-950">약관관리</h1>
            <MenuGuideButton slug="data-management" title="데이터관리 가이드" />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <Badge tone={ready?.status === 'READY' ? 'green' : readyError ? 'red' : 'slate'}>
              {ready?.status === 'READY' ? '통합약관 연결됨' : readyError ? '연결 확인 필요' : '확인 중'}
            </Badge>
            {ready?.time ? <span>{formatDateTime(ready.time)}</span> : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex min-h-[2.5rem] items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            onClick={() => {
              loadReady()
              loadCollectionStatus()
              loadDocuments()
            }}
            disabled={busy}
          >
            <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
            새로고침
          </button>
        </div>
      </div>

      {error ? <InlineError message={error} /> : null}
      {readyError ? <InlineError message={readyError} /> : null}
      {statusMessage ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
          {statusMessage}
        </div>
      ) : null}

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
        <TabButton active={activeTab === 'status'} onClick={() => setActiveTab('status')}>약관현황</TabButton>
        <TabButton active={activeTab === 'analysis'} onClick={() => setActiveTab('analysis')}>약관 분석</TabButton>
      </div>

      {activeTab === 'status' ? (
        <section className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <StatTile label="수집 소스" value={formatNumber(collectionStatus?.sourceCount)} sub="보험사 공시실 기준" />
            <StatTile label="다운로드 완료" value={formatNumber(collectionStatus?.downloadedCount)} sub="기존 동일 포함" />
            <StatTile label="파싱 완료" value={formatNumber(collectionStatus?.parsedCount)} sub="본문 검색 가능" />
            <StatTile label="파싱 대기" value={formatNumber(collectionStatus?.pendingParseCount)} sub="워커 처리 필요" />
            <StatTile label="처리 대기" value={formatNumber((collectionStatus?.pendingCollectionRunCount || 0) + (collectionStatus?.pendingDownloadCount || 0))} sub="수집·다운로드 큐" />
            <StatTile label="최근 업데이트" value={formatDateTime(collectionStatus?.latestUpdatedAt)} sub="수집·파싱 기준" />
          </div>

          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-950">
                <BarChart3 className="h-4 w-4 text-slate-500" />
                보험사별 약관 현황
              </div>
              <div className="text-xs font-semibold text-slate-500">{formatNumber(statusItems.length)}개 소스</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-xs font-semibold text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">보험사</th>
                    <th className="px-4 py-3 text-left">상태</th>
                    <th className="px-4 py-3 text-right">다운로드</th>
                    <th className="px-4 py-3 text-right">파싱 완료</th>
                    <th className="px-4 py-3 text-right">본문</th>
                    <th className="px-4 py-3 text-left">최근 업데이트</th>
                    <th className="px-4 py-3 text-left">최근 실행</th>
                    <th className="px-4 py-3 text-right">작업</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {statusItems.map((item) => {
                    const latestRun = item.latestRun || {}
                    const activeRun = ['PENDING', 'RUNNING'].includes(latestRun.status)
                    const refreshing = String(refreshingSourceId) === String(item.sourceId)
                    return (
                      <tr key={item.sourceId || item.sourceUrl} className="align-top hover:bg-slate-50/70">
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <div className="font-bold text-slate-950">{insurerLabel(item.insurerCode)}</div>
                            <div className="flex flex-wrap gap-1">
                              <Badge tone={item.enabled ? 'green' : 'slate'}>{item.enabled ? '활성' : '비활성'}</Badge>
                              <Badge tone="slate">{collectionScheduleLabel(item.collectionSchedule)}</Badge>
                            </div>
                            {item.sourceUrl ? (
                              <a
                                className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline"
                                href={item.sourceUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                공시실 열기
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <Badge tone={statusTone(item.operationalStatus)}>
                              {statusLabel(item.operationalStatus, OPERATIONAL_STATUS_LABELS)}
                            </Badge>
                            {Number(item.pendingCollectionRunCount || 0) > 0 ? (
                              <span className="text-xs text-slate-500">수집 대기 {formatNumber(item.pendingCollectionRunCount)}</span>
                            ) : null}
                            {Number(item.pendingDownloadCount || 0) > 0 ? (
                              <span className="text-xs text-slate-500">다운로드 대기 {formatNumber(item.pendingDownloadCount)}</span>
                            ) : null}
                            {Number(item.adapterRequiredCount || 0) > 0 ? (
                              <span className="text-xs font-semibold text-amber-700">전용 수집기 확인 필요</span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">
                          {formatNumber(item.downloadedCount)}
                          <div className="text-xs font-medium text-slate-500">발견 {formatNumber(item.discoveredCount)}</div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="font-semibold text-slate-900">{formatNumber(item.parsedCount)}</div>
                          {Number(item.pendingParseCount || 0) > 0 ? (
                            <div className="text-xs font-medium text-amber-700">대기 {formatNumber(item.pendingParseCount)}</div>
                          ) : null}
                          {Number(item.failedCount || 0) > 0 ? (
                            <div className="text-xs font-medium text-red-700">오류 {formatNumber(item.failedCount)}</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatNumber(item.chunkCount)}</td>
                        <td className="px-4 py-3 text-slate-700">{formatDateTime(item.latestUpdatedAt)}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <Badge tone={statusTone(latestRun.status)}>{statusLabel(latestRun.status, RUN_STATUS_LABELS)}</Badge>
                            <span className="text-xs text-slate-500">{formatDateTime(latestRun.finishedAt || latestRun.startedAt || latestRun.createdAt)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand-navy px-3 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
                            onClick={() => triggerCollectionRun(item)}
                            disabled={refreshing || activeRun || !item.sourceId}
                          >
                            {refreshing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                            {activeRun ? '진행 중' : '갱신'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {!statusItems.length && !statusLoading ? <EmptyState>약관 수집 소스가 없습니다.</EmptyState> : null}
            {statusLoading ? <EmptyState>현황을 불러오는 중</EmptyState> : null}
          </div>
        </section>
      ) : (
        <section className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile label="조회된 문서" value={formatNumber(documentMeta?.returnedCount ?? documents.length)} sub={`전체 ${formatNumber(documentMeta?.totalCount ?? documents.length)}건`} />
            <StatTile label="수집됨" value={formatNumber(collectedCount)} sub={`현재 목록 ${formatNumber(documents.length)}건 기준`} />
            <StatTile label="검색 가능" value={formatNumber(usableCount)} sub={selectedDocument ? insurerLabel(selectedDocument.insurerCode) : '문서 선택 전'} />
            <StatTile label="본문 조각" value={formatNumber(chunks.length)} sub={detail?.termsTitle || selectedDocument?.termsTitle || '-'} />
          </div>

          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-200 p-4">
              <div className="grid gap-3 lg:grid-cols-[11rem_1fr_10rem_8rem_auto]">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-slate-500">보험사</span>
                  <select
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    value={filters.insurerCode}
                    onChange={(e) => setFilters((prev) => ({ ...prev, insurerCode: e.target.value }))}
                  >
                    <option value="">전체</option>
                    {INSURER_OPTIONS.map(([code, label]) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-slate-500">검색어</span>
                  <input
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    value={filters.q}
                    onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') loadDocuments()
                    }}
                    placeholder="상품명 또는 약관명"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-slate-500">상태</span>
                  <select
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    value={filters.status}
                    onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="">전체 수집 문서</option>
                    <option value="COMPLETED">파싱 완료</option>
                    <option value="DOWNLOADED">다운로드 완료</option>
                    <option value="UNCHANGED">기존 동일 문서</option>
                    <option value="FAILED">오류 문서</option>
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-slate-500">건수</span>
                  <select
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    value={filters.limit}
                    onChange={(e) => setFilters((prev) => ({ ...prev, limit: Number(e.target.value) }))}
                  >
                    {TERMS_LIMIT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <button
                  type="button"
                  className="inline-flex h-10 items-center justify-center gap-2 self-end rounded-lg bg-brand-navy px-4 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
                  onClick={loadDocuments}
                  disabled={documentsLoading}
                >
                  <Search className="h-4 w-4" />
                  조회
                </button>
              </div>
            </div>

            <div className="grid min-h-[34rem] xl:grid-cols-[minmax(0,1.45fr)_minmax(28rem,0.95fr)]">
              <div className="min-w-0 border-b border-slate-200 bg-slate-50/50 xl:border-b-0 xl:border-r">
                <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                    <Database className="h-4 w-4 text-slate-500" />
                    약관 문서
                  </div>
                  <div className="text-xs font-semibold text-slate-500">{formatNumber(documents.length)}건 표시</div>
                </div>
                <div className="max-h-[46rem] space-y-2 overflow-auto p-3">
                  {documents.map((item) => {
                    const active = String(item.id) === String(selectedId)
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`block w-full rounded-lg border p-3 text-left transition ${
                          active
                            ? 'border-brand-sky bg-white shadow-sm ring-2 ring-sky-100'
                            : 'border-slate-200 bg-white hover:border-sky-200 hover:bg-sky-50/30'
                        }`}
                        onClick={() => setSelectedId(item.id)}
                      >
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge tone="slate">{insurerLabel(item.insurerCode)}</Badge>
                              <Badge tone={statusTone(item.parseStatus || item.itemStatus)}>{documentStatusLabel(item)}</Badge>
                              {item.salePeriodText ? (
                                <span className="truncate text-xs font-medium text-slate-500">{item.salePeriodText}</span>
                              ) : null}
                            </div>
                            <div className="mt-2 line-clamp-2 text-base font-bold text-slate-950">{item.productName || '-'}</div>
                            <div className="mt-1 line-clamp-2 text-sm text-slate-600">{item.termsTitle || '-'}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-right lg:w-32">
                            <div className="rounded-md bg-slate-50 px-2 py-1.5">
                              <div className="text-[11px] font-semibold text-slate-500">페이지</div>
                              <div className="text-sm font-bold text-slate-900">{formatNumber(item.pageCount)}</div>
                            </div>
                            <div className="rounded-md bg-slate-50 px-2 py-1.5">
                              <div className="text-[11px] font-semibold text-slate-500">본문</div>
                              <div className="text-sm font-bold text-slate-900">{formatNumber(item.chunkCount)}</div>
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                  {!documents.length && !documentsLoading ? <EmptyState>조회된 약관 문서가 없습니다.</EmptyState> : null}
                  {documentsLoading ? <EmptyState>문서를 불러오는 중</EmptyState> : null}
                </div>
              </div>

              <div className="min-w-0 p-4">
                {!selectedId ? (
                  <EmptyState>문서를 선택하세요.</EmptyState>
                ) : detailLoading ? (
                  <EmptyState>상세 조회 중</EmptyState>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h2 className="line-clamp-2 text-lg font-bold text-slate-950">{detail?.productName || selectedDocument?.productName || '-'}</h2>
                          <p className="mt-1 line-clamp-2 text-sm text-slate-600">{detail?.termsTitle || selectedDocument?.termsTitle || '-'}</p>
                        </div>
                        {detail?.pdfUrl ? (
                          <a
                            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                            href={detail.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ExternalLink className="h-4 w-4" />
                            PDF
                          </a>
                        ) : null}
                      </div>
                      <div className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3">
                        <DetailRow label="보험사">{insurerLabel(detail?.insurerCode || selectedDocument?.insurerCode)}</DetailRow>
                        <DetailRow label="수집상태">{statusLabel(detail?.itemStatus || selectedDocument?.itemStatus, ITEM_STATUS_LABELS)}</DetailRow>
                        <DetailRow label="파싱상태">{statusLabel(detail?.parseStatus || selectedDocument?.parseStatus, PARSE_STATUS_LABELS)}</DetailRow>
                        <DetailRow label="수집일시">{formatDateTime(detail?.collectedAt || selectedDocument?.collectedAt)}</DetailRow>
                        <DetailRow label="페이지">{`${formatNumber(detail?.pageCount || selectedDocument?.pageCount)}쪽`}</DetailRow>
                        <DetailRow label="본문">{`${formatNumber(detail?.chunkCount || selectedDocument?.chunkCount)}개`}</DetailRow>
                        <DetailRow label="출처">{`${insurerLabel(detail?.insurerCode || selectedDocument?.insurerCode)} 공식 공시실`}</DetailRow>
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                          <Layers className="h-4 w-4 text-slate-500" />
                          본문 조각
                        </div>
                        <span className="text-xs text-slate-500">{formatNumber(chunks.length)}건</span>
                      </div>
                      <div className="max-h-[23rem] space-y-2 overflow-auto pr-1">
                        {chunks.slice(0, 30).map((chunk) => (
                          <button
                            key={chunk.chunkId || `${chunk.documentId}-${chunk.chunkIndex}`}
                            type="button"
                            className="block w-full rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-brand-sky/50 hover:bg-sky-50/40"
                            onClick={() => {
                              setChunkQuery((chunk.text || '').slice(0, 30).trim() || chunkQuery)
                            }}
                          >
                            <div className="mb-1 flex items-center justify-between gap-2 text-xs text-slate-500">
                              <span>{chunkLabel(chunk.chunkIndex)}</span>
                              <span>{statusLabel(chunk.embeddingStatus, EMBEDDING_STATUS_LABELS)}</span>
                            </div>
                            <div className="line-clamp-4 text-sm leading-6 text-slate-800">{chunk.text || '-'}</div>
                          </button>
                        ))}
                        {!chunks.length ? <EmptyState>파싱된 본문이 없습니다.</EmptyState> : null}
                      </div>
                    </div>

                    {ruleCandidates.length ? (
                      <div>
                        <div className="mb-2 text-sm font-bold text-slate-900">분석 후보</div>
                        <div className="space-y-2">
                          {ruleCandidates.slice(0, 5).map((candidate) => (
                            <div key={candidate.id} className="rounded-lg border border-slate-200 bg-white p-3">
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-semibold text-slate-900">{candidate.title || '분석 후보'}</div>
                                <Badge tone={statusTone(candidate.candidateStatus)}>{statusLabel(candidate.candidateStatus, CANDIDATE_STATUS_LABELS)}</Badge>
                              </div>
                              <div className="mt-1 line-clamp-3 text-sm text-slate-600">{candidate.summary || candidate.evidenceText || '-'}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <section className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-base font-bold text-slate-950">
                  <Search className="h-4 w-4 text-slate-500" />
                  본문 검색
                </div>
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand-navy px-3 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
                  onClick={runChunkSearch}
                  disabled={chunkSearchLoading}
                >
                  <Search className="h-4 w-4" />
                  검색
                </button>
              </div>
              <input
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                value={chunkQuery}
                onChange={(e) => setChunkQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runChunkSearch()
                }}
                placeholder="본문 검색어"
              />
              <div className="mt-3 max-h-[25rem] space-y-2 overflow-auto pr-1">
                {chunkResults.map((chunk) => (
                  <button
                    key={chunk.chunkId || `${chunk.documentId}-${chunk.chunkIndex}`}
                    type="button"
                    className="block w-full rounded-lg border border-slate-200 p-3 text-left hover:border-brand-sky/50 hover:bg-sky-50/40"
                    onClick={() => setSelectedId(chunk.documentId)}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs text-slate-500">
                      <span>{chunkLabel(chunk.chunkIndex)}</span>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                    <div className="line-clamp-4 text-sm leading-6 text-slate-800">{chunk.text || '-'}</div>
                  </button>
                ))}
                {!chunkResults.length ? <EmptyState>검색 결과가 없습니다.</EmptyState> : null}
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-base font-bold text-slate-950">
                  <Activity className="h-4 w-4 text-slate-500" />
                  계약 약관 매칭
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    onClick={applySelectedDocumentToMatch}
                    disabled={!selectedDocument && !detail}
                  >
                    <FileText className="h-4 w-4" />
                    선택 문서 적용
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand-navy px-3 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
                    onClick={runMatch}
                    disabled={matchLoading}
                  >
                    <Search className="h-4 w-4" />
                    실행
                  </button>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label>
                  <span className="mb-1 block text-xs font-semibold text-slate-500">보험사</span>
                  <select
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"
                    value={matchForm.insurerCode}
                    onChange={(e) => setMatchForm((prev) => ({ ...prev, insurerCode: e.target.value }))}
                  >
                    <option value="">전체</option>
                    {INSURER_OPTIONS.map(([code, label]) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="mb-1 block text-xs font-semibold text-slate-500">가입일</span>
                  <input
                    type="date"
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                    value={matchForm.saleDate}
                    onChange={(e) => setMatchForm((prev) => ({ ...prev, saleDate: e.target.value }))}
                  />
                </label>
                <label>
                  <span className="mb-1 block text-xs font-semibold text-slate-500">상품명</span>
                  <input
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                    value={matchForm.productName}
                    onChange={(e) => setMatchForm((prev) => ({ ...prev, productName: e.target.value }))}
                    placeholder="상품명"
                  />
                </label>
                <label>
                  <span className="mb-1 block text-xs font-semibold text-slate-500">담보명</span>
                  <input
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                    value={matchForm.coverageName}
                    onChange={(e) => setMatchForm((prev) => ({ ...prev, coverageName: e.target.value }))}
                    placeholder="담보명"
                  />
                </label>
                <label className="md:col-span-2">
                  <span className="mb-1 block text-xs font-semibold text-slate-500">질문</span>
                  <input
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                    value={matchForm.query}
                    onChange={(e) => setMatchForm((prev) => ({ ...prev, query: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') runMatch()
                    }}
                    placeholder="보험금, 면책, 감액 등"
                  />
                </label>
              </div>

              <div className="mt-3 max-h-[22rem] space-y-2 overflow-auto pr-1">
                {toItems(matchResult?.candidates).map((candidate, index) => {
                  const doc = candidate?.document || {}
                  return (
                    <button
                      key={`${doc.id || index}-${candidate.score}`}
                      type="button"
                      className="block w-full rounded-lg border border-slate-200 p-3 text-left hover:border-brand-sky/50 hover:bg-sky-50/40"
                      onClick={() => doc.id && setSelectedId(doc.id)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="line-clamp-2 text-sm font-semibold text-slate-900">{doc.productName || '-'}</div>
                          <div className="mt-1 line-clamp-1 text-xs text-slate-500">{insurerLabel(doc.insurerCode)} · {doc.termsTitle || '-'}</div>
                        </div>
                        <Badge tone="blue">{candidate.score ?? 0}점</Badge>
                      </div>
                      {Array.isArray(candidate.reasons) && candidate.reasons.length ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {candidate.reasons.slice(0, 4).map((reason) => <Badge key={reason}>{matchReasonLabel(reason)}</Badge>)}
                        </div>
                      ) : null}
                    </button>
                  )
                })}
                {matchResult && !toItems(matchResult?.candidates).length ? <EmptyState>매칭 결과가 없습니다.</EmptyState> : null}
                {!matchResult ? <EmptyState>조건을 입력하고 실행하세요.</EmptyState> : null}
              </div>
            </section>
          </div>
        </section>
      )}
    </div>
  )
}
